import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Save,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  AlertCircle,
  Plus,
  Building2,
  Calendar,
  MessageSquare,
  Gauge,
  FileText,
  Trash2,
  X,
} from "lucide-react";
import api from "../../services/api";
import { useAuth } from "../hooks/useAuth";
import { useAccountContext } from "../context/AccountContext";
import type {
  TripReportDraft,
  DetectedOrder,
  Attendee,
  Topic,
  ActionItem,
  VisitType,
  Sentiment,
  SaveTripReportResponse,
} from "../types";
import { AttendeeEditor } from "../components/AttendeeEditor";
import { TopicCard } from "../components/TopicCard";
import { ActionItemEditor } from "../components/ActionItemEditor";
import { OrderReviewCard } from "../components/OrderReviewCard";
import { ResolvedAccountBadge } from "../components/ResolvedAccountBadge";
import { AccountCombobox } from "../components/AccountCombobox";
import { AccountProductHistory } from "../components/AccountProductHistory";
import { cn } from "../lib/utils";
import { getConfidenceColor } from "../../utils/formatting";
import styles from "./ReviewEditScreen.module.css";

const VISIT_TYPES: { value: VisitType; label: string }[] = [
  { value: "in_person", label: "In-person" },
  { value: "phone_call", label: "Phone" },
  { value: "email", label: "Email" },
  { value: "other", label: "Other" },
];

const SENTIMENT_OPTIONS: { value: Sentiment; label: string }[] = [
  { value: "positive", label: "Positive" },
  { value: "neutral", label: "Neutral" },
  { value: "negative", label: "Negative" },
  { value: "mixed", label: "Mixed" },
];

const AUTOSAVE_INTERVAL_MS = 30_000;
const AUTOSAVE_KEY = "copilot_trip_draft";

function getConfidenceLabel(confidence: number): string {
  const pct = Math.round(confidence * 100);
  if (confidence >= 0.8) return `${pct}% — High`;
  if (confidence >= 0.6) return `${pct}% — Medium`;
  return `${pct}% — Low`;
}

function todayISO(): string {
  const d = new Date();
  // Use local date components, not UTC — toISOString() returns the UTC
  // date which can be a different day late at night in US timezones.
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * ReviewEditScreen — the trust-building step where salespeople verify
 * and edit the AI-extracted trip report before saving.
 *
 * Receives TripReportDraft via route location state.
 * Provides auto-save to localStorage and POST to API on save.
 */
export function ReviewEditScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { refetchAccounts } = useAccountContext();
  const isAdmin = user?.role === "admin" || user?.role === "ADMIN";

  // Initialise from route state (from RecordPage extraction),
  // or fall back to localStorage draft, or empty draft.
  // Always ensure visit_date is populated (default to today if null).
  const initialDraft = useRef(
    ensureDetectedOrder(
      ensureVisitDate(
        (location.state as { draft?: TripReportDraft } | null)?.draft ??
          loadDraftFromStorage() ??
          createEmptyDraft()
      )
    )
  );

  const [draft, setDraft] = useState<TripReportDraft>(initialDraft.current);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  // Flag to prevent unmount auto-save after explicit discard or successful save
  const skipUnmountSaveRef = useRef(false);

  // ----- Auto-save to localStorage -----
  useEffect(() => {
    const interval = setInterval(() => {
      if (!skipUnmountSaveRef.current) {
        saveDraftToStorage(draft);
      }
    }, AUTOSAVE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [draft]);

  // Also save on unmount (unless explicitly discarded/saved)
  useEffect(() => {
    return () => {
      if (!skipUnmountSaveRef.current) {
        saveDraftToStorage(draft);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- Field update helpers -----
  const updateField = useCallback(
    <K extends keyof TripReportDraft>(key: K, value: TripReportDraft[K]) => {
      setDraft((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const handleTopicChange = useCallback(
    (idx: number, updated: Topic) => {
      setDraft((prev) => ({
        ...prev,
        topics: prev.topics.map((t, i) => (i === idx ? updated : t)),
      }));
    },
    []
  );

  const handleTopicRemove = useCallback(
    (idx: number) => {
      setDraft((prev) => ({
        ...prev,
        topics: prev.topics.filter((_, i) => i !== idx),
      }));
    },
    []
  );

  const handleAddTopic = useCallback(() => {
    setDraft((prev) => ({
      ...prev,
      topics: [
        ...prev.topics,
        {
          subject: "",
          detail: "",
          sentiment: "neutral" as Sentiment,
          products_mentioned: [],
        },
      ],
    }));
  }, []);

  const isEditing = Boolean(draft.report_id);

  // ----- Save flow -----
  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      // Strip report_id from the payload — it's only used for routing
      const { report_id, ...payload } = draft;

      if (isEditing && report_id) {
        // Update existing report via PUT
        await api.put<SaveTripReportResponse>(
          `/copilot/trip-reports/${report_id}`,
          payload
        );
      } else {
        // Create new report via POST
        await api.post<SaveTripReportResponse>(
          "/copilot/trip-reports",
          payload
        );
      }

      // Haptic feedback
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }

      setSaveSuccess(true);

      // Refresh the accounts list so new accounts appear immediately
      // in the Accounts tab without requiring a full page reload.
      refetchAccounts();

      // Clear localStorage draft and prevent unmount re-save
      skipUnmountSaveRef.current = true;
      clearDraftFromStorage();

      // Navigate home after brief animation
      setTimeout(() => {
        navigate("/copilot/record", { replace: true });
      }, 1200);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to save trip report.";
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  }

  // ----- Discard draft flow -----
  function handleDiscardDraft() {
    skipUnmountSaveRef.current = true;
    clearDraftFromStorage();
    // Haptic feedback
    if (navigator.vibrate) {
      navigator.vibrate([30, 20, 30]);
    }
    navigate("/copilot/record", { replace: true });
  }

  // ----- Render -----
  return (
    <div className={styles.screen}>
      <div className={styles.scrollContainer}>
        <h2 className={styles.pageTitle}>
          {isEditing ? "Edit Trip Report" : "Review Trip Report"}
        </h2>
        <p className={styles.pageSubtitle}>
          {isEditing
            ? "Make your changes, then save."
            : "Here's what we heard — tweak anything that's off, then hit save."}
        </p>

        {/* Confidence Score Badge — admin only */}
        {isAdmin && (
          <div className={styles.confidenceRow}>
            <Gauge className="h-4 w-4" style={{ color: getConfidenceColor(draft.confidence) }} />
            <span
              className={styles.confidenceBadge}
              style={{
                background: getConfidenceColor(draft.confidence),
              }}
            >
              {getConfidenceLabel(draft.confidence)}
            </span>
            <span className={styles.confidenceHint}>Confidence</span>
          </div>
        )}

        {/* ===== Account ===== */}
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>
            <Building2 className="h-4 w-4" />
            Account
          </legend>
          <AccountCombobox
            value={draft.account_name ?? ""}
            accountUuid={draft.account_uuid}
            onChange={(name, uuid) => {
              setDraft((prev) => ({
                ...prev,
                account_name: name || null,
                account_uuid: uuid,
              }));
            }}
            label=""
            placeholder="Search or type account name..."
          />
          {draft.account_uuid ? (
            <ResolvedAccountBadge
              accountUuid={draft.account_uuid}
              className="mt-1.5"
            />
          ) : draft.account_name ? (
            <div className="mt-1 flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
              <AlertCircle className="h-3 w-3" />
              <span>New account — will be created on save</span>
            </div>
          ) : null}
        </fieldset>

        {/* ===== Account Order History ===== */}
        {draft.account_uuid && (
          <AccountProductHistory
            accountUuid={draft.account_uuid}
            topicProducts={draft.topics.flatMap((t) => t.products_mentioned)}
            resolvedItemCodes={draft.topics.flatMap((t) =>
              (t.resolved_products ?? []).map((rp) => rp.sage_item_code)
            )}
          />
        )}

        {/* ===== Visit Date ===== */}
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>
            <Calendar className="h-4 w-4" />
            Visit Date
          </legend>
          <input
            type="date"
            className={styles.textInput}
            value={draft.visit_date ?? todayISO()}
            onChange={(e) =>
              updateField("visit_date", e.target.value || null)
            }
            aria-label="Visit date"
          />
        </fieldset>

        {/* ===== Visit Type ===== */}
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Visit Type</legend>
          <div className={styles.segmentedControl}>
            {VISIT_TYPES.map((vt) => (
              <button
                key={vt.value}
                type="button"
                className={cn(
                  styles.segment,
                  draft.visit_type === vt.value && styles.segmentActive
                )}
                onClick={() => updateField("visit_type", vt.value)}
              >
                {vt.label}
              </button>
            ))}
          </div>
        </fieldset>

        {/* ===== Attendees ===== */}
        <AttendeeEditor
          attendees={draft.attendees}
          onChange={(attendees: Attendee[]) =>
            updateField("attendees", attendees)
          }
          accountUuid={draft.account_uuid}
        />

        {/* ===== Topics ===== */}
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>
            <MessageSquare className="h-4 w-4" />
            Topics Discussed
          </legend>
          <div className={styles.topicsList}>
            {draft.topics.map((topic, idx) => (
              <TopicCard
                key={idx}
                topic={topic}
                index={idx}
                onChange={(updated) => handleTopicChange(idx, updated)}
                onRemove={() => handleTopicRemove(idx)}
              />
            ))}
          </div>
          <button
            type="button"
            className={styles.addTopicBtn}
            onClick={handleAddTopic}
          >
            <Plus className="h-4 w-4" />
            Add Topic
          </button>
        </fieldset>

        {/* ===== Action Items ===== */}
        <ActionItemEditor
          items={draft.action_items}
          onChange={(items: ActionItem[]) =>
            updateField("action_items", items)
          }
        />

        {/* ===== Detected Order ===== */}
        {draft.detected_order?.has_order && (
          <OrderReviewCard
            order={draft.detected_order}
            accountName={draft.account_name}
            tripReportId={draft.report_id ?? undefined}
            onChange={(updated: DetectedOrder) =>
              updateField("detected_order", updated)
            }
          />
        )}

        {/* ===== Overall Sentiment ===== */}
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Overall Sentiment</legend>
          <div className={styles.segmentedControl}>
            {SENTIMENT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={cn(
                  styles.segment,
                  draft.overall_sentiment === opt.value &&
                    styles.segmentActive
                )}
                onClick={() =>
                  updateField("overall_sentiment", opt.value)
                }
              >
                {opt.label}
              </button>
            ))}
          </div>
        </fieldset>

        {/* ===== Raw Transcript (Collapsible) ===== */}
        <fieldset className={styles.fieldset}>
          <button
            type="button"
            className={styles.transcriptToggle}
            onClick={() => setShowTranscript(!showTranscript)}
            aria-expanded={showTranscript}
          >
            <FileText className="h-4 w-4" />
            <span>Raw Transcript</span>
            {showTranscript ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
          {showTranscript && (
            <div className={styles.transcriptContent}>
              <p className={styles.transcriptText}>
                {draft.raw_transcript || "No transcript available."}
              </p>
            </div>
          )}
        </fieldset>

        {/* Error message */}
        {saveError && (
          <div className={styles.errorBanner}>
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{saveError}</span>
          </div>
        )}
      </div>

      {/* ===== Discard Confirmation Dialog ===== */}
      {showDiscardConfirm && (
        <div
          className={styles.discardOverlay}
          onClick={() => setShowDiscardConfirm(false)}
          role="presentation"
        >
          <div
            className={styles.discardDialog}
            role="alertdialog"
            aria-labelledby="discard-title"
            aria-describedby="discard-desc"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.discardIcon}>
              <Trash2 className="h-6 w-6" />
            </div>
            <h3 id="discard-title" className={styles.discardTitle}>
              Discard this trip report draft?
            </h3>
            <p id="discard-desc" className={styles.discardDesc}>
              This will permanently delete your current draft. You&apos;ll need to record or type a new one.
            </p>
            <div className={styles.discardActions}>
              <button
                type="button"
                className={styles.discardCancelBtn}
                onClick={() => setShowDiscardConfirm(false)}
              >
                <X className="h-4 w-4" />
                Keep Draft
              </button>
              <button
                type="button"
                className={styles.discardConfirmBtn}
                onClick={handleDiscardDraft}
              >
                <Trash2 className="h-4 w-4" />
                Discard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Sticky Footer ===== */}
      <div className={styles.stickyFooter}>
        {saveSuccess ? (
          <div className={styles.successBanner}>
            <CheckCircle className="h-5 w-5" />
            <span>Trip report {isEditing ? "updated" : "saved"}!</span>
          </div>
        ) : (
          <div className={styles.footerButtons}>
            <button
              type="button"
              className={styles.discardButton}
              onClick={() => setShowDiscardConfirm(true)}
              disabled={saving}
              aria-label="Discard draft"
            >
              <Trash2 className="h-5 w-5" />
            </button>
            <button
              type="button"
              className={styles.saveButton}
              onClick={handleSave}
              disabled={saving}
            >
              <Save className="h-5 w-5" />
              {saving
                ? (isEditing ? "Updating..." : "Saving...")
                : (isEditing ? "Update Trip Report" : "Save Trip Report")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ----- localStorage helpers -----

function saveDraftToStorage(draft: TripReportDraft) {
  try {
    localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(draft));
  } catch {
    // Storage full or unavailable — non-critical
  }
}

function loadDraftFromStorage(): TripReportDraft | null {
  try {
    const raw = localStorage.getItem(AUTOSAVE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as TripReportDraft;
  } catch {
    return null;
  }
}

function clearDraftFromStorage() {
  try {
    localStorage.removeItem(AUTOSAVE_KEY);
  } catch {
    // Non-critical
  }
}

/**
 * Ensure a draft always has a visit_date.
 * When the LLM extraction returns null visit_date, default to today
 * so the user always sees and saves a date.
 */
function ensureVisitDate(draft: TripReportDraft): TripReportDraft {
  if (!draft.visit_date) {
    return { ...draft, visit_date: todayISO() };
  }
  return draft;
}

/**
 * Backwards-compat: drafts saved to localStorage before the detected_order
 * field existed will not have it. Ensure it always exists.
 */
function ensureDetectedOrder(draft: TripReportDraft): TripReportDraft {
  if (!draft.detected_order) {
    return {
      ...draft,
      detected_order: {
        has_order: false,
        confidence: 0,
        line_items: [],
        ship_to: null,
        po_number: null,
        notes: null,
      },
    };
  }
  return draft;
}

function createEmptyDraft(): TripReportDraft {
  return {
    account_name: null,
    other_companies_mentioned: [],
    account_uuid: null,
    account_resolution_label: null,
    visit_date: todayISO(),
    visit_type: "in_person",
    salesperson_name: null,
    attendees: [],
    topics: [],
    action_items: [],
    competitors_mentioned: [],
    contact_updates: [],
    detected_order: {
      has_order: false,
      confidence: 0,
      line_items: [],
      ship_to: null,
      po_number: null,
      notes: null,
    },
    overall_sentiment: "neutral",
    confidence: 0,
    raw_transcript: "",
  };
}
